import type { ConditionOption } from '../conditions.types';
import { optionKey } from './conditionSelection.util';

/**
 * Every key a stored selection covers. A stored parent stands in for its whole
 * subtree, so its sub-items read as selected without being stored themselves.
 */
export const getEffectiveKeys = (
  options: ConditionOption[],
  isStored: (option: ConditionOption) => boolean
): Set<string> => {
  const keys = new Set<string>();

  const walk = (items: ConditionOption[], coveredByAncestor: boolean) => {
    items.forEach((option) => {
      const covered = coveredByAncestor || isStored(option);

      if (covered) {
        keys.add(optionKey(option));
      }

      walk(option.subItems ?? [], covered);
    });
  };

  walk(options, false);

  return keys;
};

/**
 * Whether an option's whole subtree is selected. A parent is answered for by
 * its sub-items alone, so checking every one of them reads the same as having
 * checked the parent — and leaves the parent out while any of them is missing.
 */
const isFullySelected = (
  option: ConditionOption,
  selectedKeys: Set<string>
): boolean => {
  const subItems = option.subItems ?? [];

  return subItems.length > 0
    ? subItems.every((subItem) => isFullySelected(subItem, selectedKeys))
    : selectedKeys.has(optionKey(option));
};

/**
 * The shortest list of options covering a selection. A category whose sub-items
 * are all selected persists as itself, which is what the API reads as the whole
 * category; deselecting any one sub-item drops the parent in favour of the
 * sub-items that are left.
 */
export const toMinimalCover = (
  options: ConditionOption[],
  selectedKeys: Set<string>
): ConditionOption[] =>
  options.flatMap((option) =>
    isFullySelected(option, selectedKeys)
      ? [option]
      : toMinimalCover(option.subItems ?? [], selectedKeys)
  );
